import axios from 'axios';
import config from '../config';

/**
 * Obté el mode actiu de conceptes del servidor ('DUAL' o 'CLASSIC').
 */
export const getGraphMode = async (jwtToken) => {
  try {
    const headers = jwtToken ? { Authorization: jwtToken } : {};
    const response = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/mode`, { headers });
    return response.data?.mode || 'CLASSIC';
  } catch (err) {
    console.warn('No s\'ha pogut obtenir el mode de conceptes-graph, fallback a CLASSIC:', err);
    return 'CLASSIC';
  }
};

/**
 * Obté l'arbre jeràrquic de categories (fins a 3 nivells).
 */
export const getConceptesTree = async (jwtToken, userapp) => {
  if (!userapp) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const response = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/tree`, {
    params: { userapp },
    headers
  });
  return response.data || [];
};

/**
 * Obté la llista plana d'entitats compartides (Vehicles i Persones).
 */
export const getEntitatsTransversals = async (jwtToken, userapp) => {
  if (!userapp) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const response = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/entitats`, {
    params: { userapp },
    headers
  });
  return response.data || [];
};

/**
 * Obté la llista plana completa de tots els conceptes node de l'usuari.
 */
export const getConceptesNodes = async (jwtToken, userapp) => {
  if (!userapp) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const response = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/nodes`, {
    params: { userapp },
    headers
  });
  return response.data || [];
};

const normalizeTreeNodes = (nodes) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.map(node => {
    const p = node.conceptePath || node.path;
    return {
      ...node,
      conceptePath: p,
      path: p,
      fills: normalizeTreeNodes(node.fills)
    };
  });
};

/**
 * Obté el resum jeràrquic amb rollup de saldos computat pel servidor.
 */
export const getResumJerarquic = async (jwtToken, userapp, any = null, mes = null) => {
  if (!userapp) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = { userapp };
  if (any) params.any = any;
  if (mes) params.mes = mes;

  const response = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/resum-jerarquic`, {
    params,
    headers
  });
  return normalizeTreeNodes(response.data || []);
};

/**
 * Obté el resum transversal (despesa/ingrés i desglossament per entitat: Vehicles i Persones).
 */
export const getResumTransversal = async (jwtToken, userapp, any = null) => {
  if (!userapp) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = { userapp };
  if (any) params.any = any;

  const response = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/resum-transversal`, {
    params,
    headers
  });
  return response.data || [];
};

/**
 * Obté els moviments associats a un node de l'arbre jeràrquic de categories amb desambiguació per branca.
 */
export const getMovimentsNode = async (
  jwtToken,
  userapp,
  nodeId,
  branchIds = {},
  any = null,
  mes = null,
  includeFills = true
) => {
  if (!userapp || !nodeId) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = {
    userapp,
    includeFills
  };
  if (branchIds?.id1) params.id1 = branchIds.id1;
  if (branchIds?.id2) params.id2 = branchIds.id2;
  if (branchIds?.id3) params.id3 = branchIds.id3;
  if (any !== null && any !== undefined) params.any = any;
  if (mes !== null && mes !== undefined) params.mes = mes;

  const response = await axios.get(
    `${config.apiBaseUrl}/v1/conceptes-graph/node/${nodeId}/moviments`,
    {
      params,
      headers
    }
  );
  return response.data || [];
};

/**
 * Obté el resum anual complet per a un node del graf (agregats, 12 mesos i moviments).
 * Desambigua la branca mitjançant id1, id2, id3 si s'especifiquen.
 */
export const getResumAnualNode = async (
  jwtToken,
  userapp,
  nodeId,
  any,
  branchIds = {},
  includeFills = true
) => {
  if (!userapp || !nodeId || any == null) return null;
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = {
    userapp,
    any,
    includeFills
  };
  if (branchIds?.id1) params.id1 = branchIds.id1;
  if (branchIds?.id2) params.id2 = branchIds.id2;
  if (branchIds?.id3) params.id3 = branchIds.id3;

  const response = await axios.get(
    `${config.apiBaseUrl}/v1/conceptes-graph/node/${nodeId}/resum-anual`,
    { params, headers }
  );
  return response.data;
};

/**
 * Obté el resum global complet per a un node del graf (agregats històrics, anys i tipus de moviment).
 * Desambigua la branca mitjançant id1, id2, id3 si s'especifiquen.
 */
export const getResumGlobalNode = async (
  jwtToken,
  userapp,
  nodeId,
  branchIds = {},
  includeFills = true
) => {
  if (!userapp || !nodeId) return null;
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = {
    userapp,
    includeFills
  };
  if (branchIds?.id1) params.id1 = branchIds.id1;
  if (branchIds?.id2) params.id2 = branchIds.id2;
  if (branchIds?.id3) params.id3 = branchIds.id3;

  const response = await axios.get(
    `${config.apiBaseUrl}/v1/conceptes-graph/node/${nodeId}/resum-global`,
    { params, headers }
  );
  return response.data;
};

/**
 * Obté el resum mensual complet per a un node del graf (agregats del mes i moviments).
 * Desambigua la branca mitjançant id1, id2, id3 si s'especifiquen.
 */
export const getResumMensualNode = async (
  jwtToken,
  userapp,
  nodeId,
  any,
  mes,
  branchIds = {},
  includeFills = true
) => {
  if (!userapp || !nodeId || any == null || mes == null) return null;
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = {
    userapp,
    any,
    mes,
    includeFills
  };
  if (branchIds?.id1) params.id1 = branchIds.id1;
  if (branchIds?.id2) params.id2 = branchIds.id2;
  if (branchIds?.id3) params.id3 = branchIds.id3;

  const response = await axios.get(
    `${config.apiBaseUrl}/v1/conceptes-graph/node/${nodeId}/resum-mensual`,
    { params, headers }
  );
  return response.data;
};

/**
 * Comprova si un moviment està assignat directament a un node pare (un node que té subelements o fills).
 */
export const isMovementAssignedToParentNode = (movement, tree) => {
  if (!movement || !tree) return false;
  const targetId = (typeof movement.nodeFulla === 'number' ? movement.nodeFulla : movement.nodeFulla?.id) || movement.id_node_fulla || movement.idNodeFulla;
  const targetPath = movement.conceptePath || (typeof movement.nodeFulla === 'object' ? movement.nodeFulla?.conceptePath : null);

  let node = null;
  if (targetPath) {
    node = findNodeByPath(tree, targetPath);
  }
  if (!node && targetId) {
    node = findNodeById(tree, targetId);
  }
  if (!node && movement.concepte) {
    node = findNodeByNameOrCode(tree, movement.concepte);
  }

  return Boolean(node && node.fills && Array.isArray(node.fills) && node.fills.length > 0);
};

/**
 * Aplana un arbre de conceptes generant una llista d'opcions amb el path complet.
 * Opcionalment afegeix les branques virtuals de Nivell 1 per a Vehicles i Persones si s'indica.
 */
export const flattenConceptTree = (tree = [], entitats = [], includeVirtualRoots = true) => {
  const result = [];

  const traverse = (node, parentNodes = [], inheritedConcepteClassic = null) => {
    const currentNodesChain = [...parentNodes, node];
    const currentPathNames = currentNodesChain.map(n => n.nom);
    const currentPathCodes = currentNodesChain.map(n => n.codi);
    const conceptePath = currentPathCodes.join('/');
    const pathLabel = currentPathNames.join(' > ');

    // Concepte clàssic: si el node o la relació el té, s'utilitza.
    // Si no, s'hereta del pare. Si tampoc, s'utilitza el nom/codi clàssic de l'arrel de la branca.
    const rootNode = currentNodesChain[0];
    const rootClassicCandidate = rootNode?.concepteClassic || rootNode?.nom?.toUpperCase() || rootNode?.codi;
    const concepteClassic = node.concepteClassic || inheritedConcepteClassic || rootClassicCandidate || null;

    const flatItem = {
      ...node,
      concepteClassic,
      pathNames: currentPathNames,
      pathCodes: currentPathCodes,
      conceptePath,
      pathLabel,
      isLeaf: !node.fills || node.fills.length === 0,
      nodeN1: currentNodesChain[0] || null,
      nodeN2: currentNodesChain[1] || null,
      nodeN3: currentNodesChain[2] || null,
      id_node_n1: currentNodesChain[0]?.id || null,
      id_node_n2: currentNodesChain[1]?.id || null,
      id_node_n3: currentNodesChain[2]?.id || null
    };

    result.push(flatItem);

    if (node.fills && Array.isArray(node.fills)) {
      node.fills.forEach(child => traverse(child, currentNodesChain, concepteClassic));
    }
  };

  if (Array.isArray(tree)) {
    tree.forEach(rootNode => traverse(rootNode));
  }

  // Requeriment 3: "mostrar en el selector jerarquic de conceptes els vehicles i els membres
  // en un apartat especific de vehicles i un altre de membres al final del nivell 1"
  if (includeVirtualRoots && Array.isArray(entitats) && entitats.length > 0) {
    const vehicles = entitats.filter(e => e.tipusNode === 'VEHICLE' && e.actiu !== false);
    const persones = entitats.filter(e => e.tipusNode === 'PERSONA' && e.actiu !== false);

    if (vehicles.length > 0) {
      const rootVehicles = {
        id: -1001,
        codi: 'ENT_VEHICLES',
        nom: 'Vehicles',
        tipusNode: 'CATEGORIA',
        nivell: 1,
        actiu: true,
        ordre: 998,
        fills: vehicles.map(v => ({
          ...v,
          nivell: 2,
          fills: []
        }))
      };
      traverse(rootVehicles);
    }

    if (persones.length > 0) {
      const rootPersones = {
        id: -1002,
        codi: 'ENT_PERSONES',
        nom: 'Membres',
        tipusNode: 'CATEGORIA',
        nivell: 1,
        actiu: true,
        ordre: 999,
        fills: persones.map(p => ({
          ...p,
          nivell: 2,
          fills: []
        }))
      };
      traverse(rootPersones);
    }
  }

  return result;
};

/**
 * Construeix un arbre enriquint-lo amb els apartats de Vehicles i Persones al final del nivell 1.
 */
export const buildFullTreeWithEntities = (tree = [], entitats = []) => {
  const clonedTree = JSON.parse(JSON.stringify(tree || []));

  if (Array.isArray(entitats) && entitats.length > 0) {
    const vehicles = entitats.filter(e => e.tipusNode === 'VEHICLE' && e.actiu !== false);
    const persones = entitats.filter(e => e.tipusNode === 'PERSONA' && e.actiu !== false);

    if (vehicles.length > 0) {
      clonedTree.push({
        id: -1001,
        codi: 'ENT_VEHICLES',
        nom: 'Vehicles',
        agrupacio: 'VEHICLES',
        tipusNode: 'CATEGORIA',
        tipusFlux: 'DESPESA',
        nivell: 1,
        actiu: true,
        ordre: 998,
        fills: vehicles.map(v => ({
          ...v,
          nivell: 2,
          fills: []
        }))
      });
    }

    if (persones.length > 0) {
      clonedTree.push({
        id: -1002,
        codi: 'ENT_PERSONES',
        nom: 'Membres',
        agrupacio: 'PERSONES',
        tipusNode: 'CATEGORIA',
        tipusFlux: 'DESPESA',
        nivell: 1,
        actiu: true,
        ordre: 999,
        fills: persones.map(p => ({
          ...p,
          nivell: 2,
          fills: []
        }))
      });
    }
  }

  return clonedTree;
};

/**
 * Cerca un node a l'arbre per nom o per codi de forma recursiva.
 */
export const findNodeByNameOrCode = (nodes = [], target) => {
  if (!target) return null;
  const targetUpper = target.toUpperCase();
  for (const n of nodes) {
    if (n.nom?.toUpperCase() === targetUpper || n.codi?.toUpperCase() === targetUpper) {
      return n;
    }
    if (n.fills && Array.isArray(n.fills) && n.fills.length > 0) {
      const found = findNodeByNameOrCode(n.fills, target);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Resol el nom tècnic/clàssic que el backend reconeix per a consultes de resum de concepte.
 * Assegura compatibilitat tant si es passa un nom jeràrquic ("Electricitat"),
 * el seu codi ("SUB_ELECTRICITAT"), com el nom clàssic en majúscules ("ELECTRICITAT").
 */
export const resolveConcepteName = (rawName, conceptesList = [], conceptesTree = []) => {
  if (!rawName) return '';

  // 1. Si es passa directament un objecte node amb concepteClassic assignat pel backend
  if (typeof rawName === 'object' && rawName !== null && rawName.concepteClassic) {
    return rawName.concepteClassic;
  }

  // 2. Coincidència exacta a conceptesList
  const exact = conceptesList.find(c => c.nom === rawName);
  if (exact) return exact.nom;

  // 3. Coincidència case-insensitive a conceptesList (ex: "Electricitat" -> "ELECTRICITAT")
  const rawUpper = typeof rawName === 'string' ? rawName.toUpperCase() : '';
  const matchCase = conceptesList.find(c => c.nom?.toUpperCase() === rawUpper);
  if (matchCase) return matchCase.nom;

  // 4. Cerca si correspon a un node de l'arbre
  const node = findNodeByNameOrCode(conceptesTree, rawName);
  if (node) {
    // Si el backend ja ens proporciona el concepte clàssic resolt per branca:
    if (node.concepteClassic) {
      return node.concepteClassic;
    }

    // Si el node té un nom que coincideix en majúscules amb un concepte clàssic
    const nodeNameMatch = conceptesList.find(c => c.nom?.toUpperCase() === node.nom?.toUpperCase());
    if (nodeNameMatch) return nodeNameMatch.nom;

    // Si el codi coincideix amb un concepte clàssic
    const nodeCodeMatch = conceptesList.find(c => c.nom?.toUpperCase() === node.codi?.toUpperCase());
    if (nodeCodeMatch) return nodeCodeMatch.nom;

    // Si no coincideix amb cap clàssic, provar el nom en majúscules del node
    return node.nom ? node.nom.toUpperCase() : rawUpper;
  }

  // 5. Per defecte, retornar en majúscules (ja que la BD clàssica emmagatzema els conceptes en majúscules)
  return rawUpper;
};

/**
 * Normalitza text eliminant accents, espais extres i convertint a majúscules.
 */
export const normalizeConceptText = (text) => {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
};

/**
 * Resol el concepte clàssic legacy equivalent d'un node (sigui pare o fulla).
 * Cerca primer per concepteClassic del node, després per nom/codi normalitzat (sense accents ni prefixos).
 */
export const resolveLegacyEquivalentConcept = (nodeOrName, conceptesList = [], tree = []) => {
  if (!nodeOrName) return '';

  let node = null;
  let rawName = '';

  if (typeof nodeOrName === 'object' && nodeOrName !== null) {
    node = nodeOrName;
    rawName = node.nom || '';
  } else {
    rawName = String(nodeOrName);
    node = findNodeByNameOrCode(tree, rawName);
  }

  // 1. Si el node té concepteClassic directament informat pel backend
  if (node?.concepteClassic) {
    return node.concepteClassic;
  }

  const rawNorm = normalizeConceptText(rawName);
  const codeNorm = normalizeConceptText(node?.codi);

  // 2. Coincidència directa per nom normalitzat a conceptesList
  if (rawNorm) {
    const matchNom = conceptesList.find(c => normalizeConceptText(c.nom) === rawNorm);
    if (matchNom) return matchNom.nom;
  }

  // 3. Coincidència per codi normalitzat o traient prefixos tècnics (CAT_, SUB_, ENT_)
  if (codeNorm) {
    const cleanCode = codeNorm.replace(/^(CAT_|SUB_|ENT_VEH_|ENT_PER_|ENT_)/, '');
    const matchCodi = conceptesList.find(c => {
      const cNorm = normalizeConceptText(c.nom);
      return cNorm === codeNorm || cNorm === cleanCode;
    });
    if (matchCodi) return matchCodi.nom;
  }

  // 4. Fallback a resolveConcepteName estàndard
  return resolveConcepteName(node || rawName, conceptesList, tree) || rawName;
};

/**
 * Comprova si un identificador és un ID nou de node de graf (numèric o format node:id).
 */
export const isNodeId = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim();
  if (str.startsWith('node:')) return true;
  return /^-?\d+$/.test(str);
};

/**
 * Cerca un node a l'arbre pel seu ID únic.
 */
export const findNodeById = (nodes = [], id) => {
  if (id === null || id === undefined) return null;
  const targetId = typeof id === 'string' && id.startsWith('node:')
    ? parseInt(id.replace('node:', ''), 10)
    : parseInt(id, 10);

  for (const n of nodes) {
    if (n.id === targetId) return n;
    if (n.fills && Array.isArray(n.fills) && n.fills.length > 0) {
      const found = findNodeById(n.fills, targetId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Cerca un node a l'arbre pel seu conceptePath.
 */
export const findNodeByPath = (nodes = [], path) => {
  if (!path || !Array.isArray(nodes) || nodes.length === 0) return null;
  const pathUpper = String(path).trim().toUpperCase();

  // 1. Cerca directa per camp conceptePath
  for (const n of nodes) {
    if (n.conceptePath?.toUpperCase() === pathUpper) return n;
    if (n.fills && Array.isArray(n.fills) && n.fills.length > 0) {
      const found = findNodeByPath(n.fills, path);
      if (found) return found;
    }
  }

  // 2. Cerca per segments jeràrquics (N1/N2/N3)
  const segments = pathUpper.split('/').map(s => s.trim()).filter(Boolean);
  if (segments.length > 0) {
    let currentLevel = nodes;
    let targetNode = null;
    let inheritedClassic = null;

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const match = currentLevel.find(n =>
        n.codi?.toUpperCase() === seg ||
        n.nom?.toUpperCase() === seg
      );

      if (match) {
        if (match.concepteClassic) {
          inheritedClassic = match.concepteClassic;
        }
        targetNode = match;
        currentLevel = match.fills || [];
      } else {
        targetNode = null;
        break;
      }
    }

    if (targetNode) {
      return {
        ...targetNode,
        concepteClassic: targetNode.concepteClassic || inheritedClassic
      };
    }
  }

  return null;
};

/**
 * Retorna un conjunt (Set) amb l'ID del node i tots els IDs dels seus descendents.
 */
export const getAllDescendantNodeIds = (node) => {
  const ids = new Set();
  if (!node) return ids;

  const collect = (curr) => {
    if (!curr) return;
    if (curr.id !== undefined && curr.id !== null) {
      ids.add(Number(curr.id));
    }
    if (curr.fills && Array.isArray(curr.fills)) {
      curr.fills.forEach(collect);
    }
  };

  collect(node);
  return ids;
};

/**
 * Comprova si un moviment pertany a un node o a qualsevol dels seus descendents jeràrquics.
 */
export const movementMatchesNodeOrDescendants = (movement, node, descendantIds = null) => {
  if (!movement || !node) return false;

  const validIds = descendantIds || getAllDescendantNodeIds(node);
  const movNodeId = (typeof movement.nodeFulla === 'number' ? movement.nodeFulla : movement.nodeFulla?.id) || movement.idNodeFulla || movement.id_node_fulla;

  // 1. Coincidència per ID de node (si el moviment és del node o d'algun descendent)
  if (movNodeId !== undefined && movNodeId !== null && validIds.has(Number(movNodeId))) {
    return true;
  }

  // 2. Coincidència per conceptePath (prefix de branca)
  if (node.conceptePath && movement.conceptePath) {
    const nodePathUpper = node.conceptePath.trim().toUpperCase();
    const movPathUpper = movement.conceptePath.trim().toUpperCase();
    if (movPathUpper === nodePathUpper || movPathUpper.startsWith(nodePathUpper + '/')) {
      return true;
    }
  }

  // 3. Fallback per a moviments legacy sense node_fulla assignat
  if (!movNodeId && !movement.conceptePath && movement.concepte) {
    const movConcepteNorm = normalizeConceptText(movement.concepte);
    if (movConcepteNorm === normalizeConceptText(node.nom) || movConcepteNorm === normalizeConceptText(node.codi)) {
      return true;
    }
  }

  return false;
};

/**
 * Cerca TOTS els nodes que tinguin un mateix nom (per trobar conceptes repetits com Parking en diferents branques).
 */
export const findAllNodesByName = (nodes = [], targetName, parentBreadcrumb = '') => {
  if (!targetName) return [];
  const results = [];
  const targetUpper = targetName.toUpperCase();

  const traverse = (nodeList, parentPath = '') => {
    for (const n of nodeList) {
      const currentPath = parentPath ? `${parentPath} > ${n.nom}` : n.nom;
      if (n.nom?.toUpperCase() === targetUpper || n.codi?.toUpperCase() === targetUpper) {
        results.push({
          ...n,
          fullBreadcrumb: currentPath
        });
      }
      if (n.fills && Array.isArray(n.fills) && n.fills.length > 0) {
        traverse(n.fills, currentPath);
      }
    }
  };

  traverse(nodes, parentBreadcrumb);
  return results;
};

/**
 * Construeix la cadena ordenada de nodes (i noms) des de l'arrel fins al node especificat.
 */
export const getNodeAncestry = (tree = [], target) => {
  if (!target || !Array.isArray(tree) || tree.length === 0) return [];

  const targetId = typeof target === 'number'
    ? target
    : typeof target === 'object' && target !== null
      ? (target.id_node_fulla || target.idNodeFulla || (typeof target.nodeFulla === 'number' ? target.nodeFulla : target.nodeFulla?.id) || target.id)
      : null;

  const targetPath = typeof target === 'string'
    ? target
    : typeof target === 'object' && target !== null
      ? (target.conceptePath || target.path || (typeof target.nodeFulla === 'object' ? target.nodeFulla?.conceptePath : null))
      : null;

  const targetClassic = typeof target === 'string'
    ? target
    : typeof target === 'object' && target !== null
      ? (target.concepteClassic || target.concepte || target.nom)
      : null;

  const targetClassicUpper = targetClassic ? String(targetClassic).trim().toUpperCase() : '';
  const targetPathUpper = targetPath ? String(targetPath).trim().toUpperCase() : '';

  // 1. PRIORITAT MÀXIMA: Resolució estricta per conceptePath (N1 -> N2 -> N3)
  // En un graf, la seqüència de codis N1/N2/N3 és 100% inequívoca per a cada branca.
  if (targetPathUpper) {
    const pathSegments = targetPathUpper.split('/').map(s => s.trim()).filter(Boolean);
    if (pathSegments.length > 0) {
      let currentNodes = tree;
      const resolvedChain = [];
      let allMatched = true;

      for (let i = 0; i < pathSegments.length; i++) {
        const seg = pathSegments[i];
        const match = currentNodes.find(n =>
          n.codi?.toUpperCase() === seg ||
          n.nom?.toUpperCase() === seg
        );

        if (match) {
          resolvedChain.push(match);
          currentNodes = match.fills || [];
        } else {
          allMatched = false;
          break;
        }
      }

      if (allMatched && resolvedChain.length === pathSegments.length) {
        return resolvedChain;
      }
    }
  }

  // 2. PRIORITAT 2: Coincidència per ID validant el concepte clàssic de la branca
  // Evita que un node compartit (com un vehicle present a Assegurances i Préstecs) s'assigni a la branca equivocada.
  if (targetId && targetClassicUpper) {
    const findBranchWithClassic = (nodes, currentChain = []) => {
      for (const node of nodes) {
        const newChain = [...currentChain, node];
        if (Number(node.id) === Number(targetId)) {
          const branchMatches = newChain.some(n =>
            n.concepteClassic?.toUpperCase() === targetClassicUpper ||
            n.codi?.toUpperCase() === targetClassicUpper ||
            n.nom?.toUpperCase() === targetClassicUpper
          );
          if (branchMatches) return newChain;
        }
        if (node.fills && Array.isArray(node.fills) && node.fills.length > 0) {
          const found = findBranchWithClassic(node.fills, newChain);
          if (found) return found;
        }
      }
      return null;
    };

    const matchedBranch = findBranchWithClassic(tree);
    if (matchedBranch) return matchedBranch;
  }

  // 3. PRIORITAT 3: Coincidència directa per concepteClassic
  if (targetClassicUpper) {
    const findByClassic = (nodes, currentChain = []) => {
      for (const node of nodes) {
        if (node.actiu === false) continue;
        const newChain = [...currentChain, node];
        if (node.concepteClassic && node.concepteClassic.trim().toUpperCase() === targetClassicUpper) {
          return newChain;
        }
        if (node.fills && Array.isArray(node.fills) && node.fills.length > 0) {
          const found = findByClassic(node.fills, newChain);
          if (found) return found;
        }
      }
      return null;
    };
    const matchedClassic = findByClassic(tree);
    if (matchedClassic) return matchedClassic;
  }

  // 4. PRIORITAT 4: Cerca per ID únic com a fallback
  if (targetId) {
    const findById = (nodes, currentChain = []) => {
      for (const node of nodes) {
        const newChain = [...currentChain, node];
        if (Number(node.id) === Number(targetId)) {
          return newChain;
        }
        if (node.fills && Array.isArray(node.fills) && node.fills.length > 0) {
          const found = findById(node.fills, newChain);
          if (found) return found;
        }
      }
      return null;
    };
    const matchedId = findById(tree);
    if (matchedId) return matchedId;
  }

  return [];
};

/**
 * Formata de forma intel·ligent i compacta la ruta jeràrquica per mostrar a la UI amb els noms dels nodes.
 * Evita repetir informació redundant ja mostrada pel concepte clàssic.
 */
export const formatSmartConceptPath = (classicName = '', nodeOrPathInfo = null, tree = []) => {
  const ancestry = getNodeAncestry(tree, nodeOrPathInfo || classicName);

  if (!ancestry || ancestry.length === 0) {
    return {
      displayPath: '',
      fullBreadcrumb: ''
    };
  }

  const nameChain = ancestry.map(n => n.nom);
  const fullBreadcrumb = nameChain.join(' > ');

  const classicNorm = normalizeConceptText(classicName);
  const firstName = nameChain[0];
  const lastName = nameChain[nameChain.length - 1];
  const firstNameNorm = normalizeConceptText(firstName);
  const lastNameNorm = normalizeConceptText(lastName);

  // Si només té 1 nivell
  if (nameChain.length === 1) {
    if (firstNameNorm === classicNorm) {
      return { displayPath: '', fullBreadcrumb };
    }
    return { displayPath: firstName, fullBreadcrumb };
  }

  // Si té 2 o més nivells:
  // Cas 1: El concepte clàssic coincideix amb la fulla final (el cas més habitual)
  // Ex: clàssic 'ELECTRICITAT' i ruta 'Habitatge > Subministraments > Electricitat'
  // Mostrem l'inici acabat en ' /..' -> 'Habitatge / Subministraments /..'
  if (lastNameNorm === classicNorm) {
    const parentParts = nameChain.slice(0, -1);
    return {
      displayPath: `${parentParts.join(' / ')} /..`,
      fullBreadcrumb
    };
  }

  // Cas 2: El concepte clàssic coincideix amb l'arrel inicial
  // Ex: clàssic 'VEHICLES' i ruta 'Vehicles > Cotxe 1 > Assegurança'
  // Mostrem el final començant per '../ ' -> '../ Cotxe 1 / Assegurança'
  if (firstNameNorm === classicNorm) {
    const childParts = nameChain.slice(1);
    return {
      displayPath: `../ ${childParts.join(' / ')}`,
      fullBreadcrumb
    };
  }

  // Cas 3: El concepte clàssic és diferent tant de l'arrel com de la fulla final
  // (per exemple, s'ha canviat el nom entre concepte antic i nou: clàssic 'GAS' vs nou 'Gas Natural')
  return {
    displayPath: nameChain.join(' / '),
    fullBreadcrumb
  };
};

/**
 * Resol el nom del concepte i la ruta visual per a un moviment o subdivisió.
 * Utilitza exclusivament els IDs dels nodes (nodeN1, nodeN2, nodeN3, nodeFulla) com a font de veritat,
 * calculant el path únicament per a la presentació visual.
 * Mai utilitza conceptePath per a càlculs.
 */
export const resolveMovementConceptAndPath = (movement, tree = [], conceptesNodesMap = null) => {
  if (!movement) {
    return {
      conceptName: '',
      displayPath: '',
      fullBreadcrumb: '',
      isAssignedToParent: false
    };
  }

  const getNodeById = (raw) => {
    if (!raw) return null;
    const id = typeof raw === 'object' ? raw.id : raw;
    if (id == null) return null;
    const idNum = Number(id);
    if (typeof raw === 'object' && raw.nom) return raw;
    if (conceptesNodesMap && conceptesNodesMap.has(idNum)) {
      return conceptesNodesMap.get(idNum);
    }
    return findNodeById(tree, idNum);
  };

  const idFulla = movement.nodeFulla?.id || movement.id_node_fulla || movement.idNodeFulla || (typeof movement.nodeFulla === 'number' ? movement.nodeFulla : null);
  const idN1 = movement.nodeN1?.id || movement.id_node_n1 || movement.idNodeN1 || (typeof movement.nodeN1 === 'number' ? movement.nodeN1 : null);
  const idN2 = movement.nodeN2?.id || movement.id_node_n2 || movement.idNodeN2 || (typeof movement.nodeN2 === 'number' ? movement.nodeN2 : null);
  const idN3 = movement.nodeN3?.id || movement.id_node_n3 || movement.idNodeN3 || (typeof movement.nodeN3 === 'number' ? movement.nodeN3 : null);

  const hasHierarchy = Boolean(idFulla || idN1);

  if (hasHierarchy) {
    const nodeFulla = getNodeById(movement.nodeFulla || idFulla);
    const nodeN1 = getNodeById(movement.nodeN1 || idN1);
    const nodeN2 = getNodeById(movement.nodeN2 || idN2);
    const nodeN3 = getNodeById(movement.nodeN3 || idN3);

    // Cadena ordenada de nodes únics de la branca:
    const chain = [];
    const addedIds = new Set();
    [nodeN1, nodeN2, nodeN3, nodeFulla].forEach(n => {
      if (n && n.id != null && !addedIds.has(n.id)) {
        addedIds.add(n.id);
        chain.push(n);
      }
    });

    const effectiveLeaf = nodeFulla || (chain.length > 0 ? chain[chain.length - 1] : null);
    const conceptName = effectiveLeaf?.nom || 'PENDENT';
    const conceptCode = effectiveLeaf?.codi || effectiveLeaf?.concepteClassic || movement.concepte || '';

    let displayPath = '';
    let fullBreadcrumb = '';

    if (chain.length > 1) {
      // La fulla té pares (ex: [Alimentació, Súper])
      const parents = chain.slice(0, -1);
      displayPath = `${parents.map(p => p.nom).join(' / ')} /..`;
      fullBreadcrumb = chain.map(n => n.nom).join(' > ');
    } else if (chain.length === 1) {
      // Node arrel de nivell 1 (ex: [Alimentació])
      displayPath = '';
      fullBreadcrumb = chain[0].nom;
    }

    const isAssignedToParent = isMovementAssignedToParentNode(
      {
        id_node_fulla: effectiveLeaf?.id,
        concepte: effectiveLeaf?.nom
      },
      tree
    );

    return {
      conceptName,
      conceptCode,
      nodeId: effectiveLeaf?.id || null,
      displayPath,
      fullBreadcrumb,
      isAssignedToParent
    };
  }

  // Cas 2: Moviment clàssic / legacy (sense cap node de graf)
  const classic = movement.concepte || 'PENDENT';
  const ancestry = getNodeAncestry(tree, classic);

  if (ancestry && ancestry.length > 0) {
    const effectiveLeaf = ancestry[ancestry.length - 1];
    const conceptName = effectiveLeaf?.nom || classic;
    const conceptCode = effectiveLeaf?.codi || effectiveLeaf?.concepteClassic || classic;
    let displayPath = '';
    let fullBreadcrumb = '';

    if (ancestry.length > 1) {
      const parents = ancestry.slice(0, -1);
      displayPath = `${parents.map(p => p.nom).join(' / ')} /..`;
      fullBreadcrumb = ancestry.map(n => n.nom).join(' > ');
    } else if (ancestry.length === 1) {
      displayPath = '';
      fullBreadcrumb = ancestry[0].nom;
    }

    const isAssignedToParent = isMovementAssignedToParentNode(
      {
        id_node_fulla: effectiveLeaf?.id,
        concepte: effectiveLeaf?.nom
      },
      tree
    );

    return {
      conceptName,
      conceptCode,
      nodeId: effectiveLeaf?.id || null,
      displayPath,
      fullBreadcrumb,
      isAssignedToParent
    };
  }

  return {
    conceptName: classic,
    conceptCode: classic,
    nodeId: null,
    displayPath: '',
    fullBreadcrumb: '',
    isAssignedToParent: false
  };
};

/**
 * Obté els candidats a refinament jeràrquic (moviments amb concepte genèric N1/N2 i proposta N2/N3).
 * Suporta filtratge per estat: 'PENDENTS', 'DESCARTATS' o 'TOTS'.
 */
export const getRefineCandidates = async (
  jwtToken,
  userapp,
  any = null,
  minConfidence = 0.90,
  estat = 'PENDENTS'
) => {
  if (!userapp) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = { userapp };
  if (any && any !== 'ALL') params.any = any;
  if (minConfidence != null) params.minConfidence = minConfidence;
  if (estat) params.estat = estat;

  const response = await axios.get(
    `${config.apiBaseUrl}/v1/conceptes-graph/refine/candidates`,
    { params, headers }
  );
  return response.data || [];
};

/**
 * Executa una acció de refinament individual o en massa per a moviments iguals.
 * itemData: { concepteMovimentId, accio: 'ACCEPTAR'|'DESCARTAR'|'MANUAL'|'RESTAURAR', conceptePath, nodeFullaId, aplicarATotsElsIguals }
 * Retorna RefineResultDTO: { concepteMovimentId, accio, conceptePath, nodeFullaId, afectatsCount, success, message }
 */
export const executeRefineItem = async (jwtToken, userapp, itemData) => {
  if (!userapp || !itemData) return null;
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = { userapp };

  const response = await axios.post(
    `${config.apiBaseUrl}/v1/conceptes-graph/refine/item`,
    itemData,
    { params, headers }
  );
  return response.data;
};

/**
 * Executa l'aplicació del refinament jeràrquic (de forma massiva o per llista de seleccions confirmades).
 */
export const executeRefine = async (jwtToken, userapp, any = null, items = null) => {
  if (!userapp) return null;
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = { userapp };
  if (any && any !== 'ALL') params.any = any;

  // Si no s'informa items o és buit, enviem cos buit per a aprovació automàtica del backend
  const body = (items && items.length > 0) ? items : [];

  const response = await axios.post(
    `${config.apiBaseUrl}/v1/conceptes-graph/refine/execute`,
    body,
    { params, headers }
  );
  return response.data;
};

/**
 * Provador semàntic en temps real per classificar un text lliure o descripció d'Amazon.
 */
export const classifyText = async (jwtToken, userapp, text) => {
  if (!userapp || !text) return null;
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = { userapp, text };

  const response = await axios.get(
    `${config.apiBaseUrl}/v1/conceptes-graph/classify-text`,
    { params, headers }
  );
  return response.data;
};

/**
 * Obté l'auditoria d'alineació jeràrquica entre conceptes clàssics i de graf.
 */
export const getMovimentsAlignment = async (jwtToken, userapp) => {
  if (!userapp) return [];
  const headers = jwtToken ? { Authorization: jwtToken } : {};
  const params = { userapp };

  const response = await axios.get(
    `${config.apiBaseUrl}/v1/conceptes-graph/admin/moviments-alignment`,
    { params, headers }
  );
  return response.data || [];
};
